import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const raw = (await res.text()) || res.statusText;
    let message = raw;
    try {
      const parsed = JSON.parse(raw);
      message = parsed?.error || parsed?.message || raw;
    } catch {
      // not JSON; keep raw text
    }
    throw new Error(String(message));
  }
}

async function refreshTokenIfNeeded(): Promise<string | null> {
  const token = localStorage.getItem('jwt_token');
  if (!token) return null;

  try {
    // Try to refresh the token
    const response = await fetch('/api/auth/refresh-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (response.ok) {
      const result = await response.json();
      localStorage.setItem('jwt_token', result.token);
      return result.token;
    } else {
      // Refresh failed, clear token
      localStorage.removeItem('jwt_token');
      return null;
    }
  } catch (error) {
    console.error('Token refresh error:', error);
    localStorage.removeItem('jwt_token');
    return null;
  }
}

export async function apiRequest(
  url: string,
  options: { method?: string; body?: unknown } = {}
): Promise<any> {
  const { method = "GET", body: data } = options;
  const makeRequest = async (authToken: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};

    if (data) {
      headers["Content-Type"] = "application/json";
    }

    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }

    // Append public token to API requests if present
    let finalUrl = url;
    try {
      const publicToken = sessionStorage.getItem('public_token');
      if (publicToken && typeof url === 'string' && url.startsWith('/api')) {
        const hasQuery = url.includes('?');
        finalUrl = `${url}${hasQuery ? '&' : '?'}t=${encodeURIComponent(publicToken)}`;
      }
    } catch {}

    return fetch(finalUrl, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });
  };

  const token = localStorage.getItem('jwt_token');
  let res = await makeRequest(token);

  // If we get a 401, try to refresh the token and retry once
  if (res.status === 401 && token) {
    const newToken = await refreshTokenIfNeeded();
    if (newToken) {
      res = await makeRequest(newToken);
    }
  }

  await throwIfResNotOk(res);
  // Some endpoints may return 204 No Content
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const makeRequest = async (authToken: string | null): Promise<Response> => {
      const headers: Record<string, string> = {};

      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      // Append public token to API query URLs if present
      let finalUrl = queryKey[0] as string;
      try {
        const publicToken = sessionStorage.getItem('public_token');
        if (publicToken && typeof finalUrl === 'string' && finalUrl.startsWith('/api')) {
          const hasQuery = finalUrl.includes('?');
          finalUrl = `${finalUrl}${hasQuery ? '&' : '?'}t=${encodeURIComponent(publicToken)}`;
        }
      } catch {}

      return fetch(finalUrl, {
        headers,
      });
    };

    const token = localStorage.getItem('jwt_token');
    let res = await makeRequest(token);

    // If we get a 401, try to refresh the token and retry once
    if (res.status === 401 && token) {
      const newToken = await refreshTokenIfNeeded();
      if (newToken) {
        res = await makeRequest(newToken);
      }
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 10 * 60 * 1000, // 10 minutes - admin data changes infrequently
      gcTime: 30 * 60 * 1000, // 30-minute cache retention (renamed from cacheTime in v5)
      retry: 1, // Single retry for network issues
      refetchOnMount: false, // Reduce unnecessary refetches
      networkMode: 'online', // Only fetch when online
    },
    mutations: {
      retry: 1,
      networkMode: 'online',
      // Automatically invalidate related queries after mutations
      onSuccess: () => {
        // This will be overridden by specific mutations as needed
      },
    },
  },
});
